// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2023 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import useAppSelector from "@insite/client-framework/Common/Hooks/useAppSelector";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { HasProductContext, withProductContext } from "@insite/client-framework/Components/ProductContext";
import siteMessage from "@insite/client-framework/SiteMessage";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getCurrentPage, getLocation } from "@insite/client-framework/Store/Data/Pages/PageSelectors";
import translate from "@insite/client-framework/Translate";
import Button, { ButtonPresentationProps } from "@insite/mobius/Button";
import GridContainer, { GridContainerProps } from "@insite/mobius/GridContainer";
import { GridItemProps } from "@insite/mobius/GridItem";
import GridItem from "@insite/mobius/GridItem";
import TextArea from "@insite/mobius/TextArea";
import TextField from "@insite/mobius/TextField";
import ToasterContext from "@insite/mobius/Toast/ToasterContext";
import { useHistory } from "@insite/mobius/utilities/HistoryContext";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import requestMoreInfo from "@werner/App/Handlers/Products/RequestProductInfo";
import useCurrentPageHref from "@werner/App/Hooks/useCurrentPageHref";
// XNG-Changes: import twobuttonmodal from override
import Modal, { ModalPresentationProps } from "@werner/MobiusOverrides/Modal/Modal";
import Email from "@werner/XDL/Icons/Email";
import { modalButtonsActionStyles, xScreen } from "@werner/XDL/Utilities";
import { mergeWithCss } from "@werner/XDL/Utilities/mergeWithCss";
import { xValidateOnSubmit } from "@werner/XDL/Utilities/xValidate/xFieldValidator";
import * as React from "react";
import { FC, useContext, useEffect, useState } from "react";
import { connect, ResolveThunks } from "react-redux";
import styled, { css } from "styled-components";

const StyledForm = styled.form``;

const mapStateToProps = (state: ApplicationState, ownProps: HasProductContext) => {
    const currentPage = getCurrentPage(state)?.type;
    const currentSelectedProduct = state.pages.productDetails.selectedProductId || "";
    const isProductDetailsPage = currentPage === "ProductDetailsPage";

    return {
        isProductDetailsPage,
        isCurrentSelectedProductInPDP: currentSelectedProduct === ownProps.productContext.product.id,
    };
};

const mapDispatchToProps = {
    requestMoreInfo,
};

export interface MoreInfoButtonProps {
    extendedStyles?: MoreInfoButtonStyle;
}

type Props = MoreInfoButtonProps &
    HasProductContext &
    ResolveThunks<typeof mapDispatchToProps> &
    ReturnType<typeof mapStateToProps>;

export interface MoreInfoButtonStyle {
    button?: ButtonPresentationProps;
    formContainer?: GridContainerProps;
    halfGridItem?: GridItemProps;
    fullGridItem?: GridItemProps;
    modalButtonsWrapper?: InjectableCss;
    submitButton?: ButtonPresentationProps;
    cancelButton?: ButtonPresentationProps;
    modal?: ModalPresentationProps;
    defaultButton?: ButtonPresentationProps;
}

const moreInfoButtonStyle: MoreInfoButtonStyle = {
    button: {
        css: css`
            &&& {
                width: 100%;
                max-width: 100%;
            }
        `,
        icon: {
            src: Email,
            position: "right",
            ...{ color: "secondary", size: 26 },
        },
        ...({ hoverAnimation: "vibration" } as any),
    },
    defaultButton: {
        css: css`
            &&& {
                width: 100%;
                max-width: 100%;
            }
        `,
    },
    formContainer: {
        gap: 24,
    },
    halfGridItem: {
        width: [12, 12, 6, 6, 6],
    },
    fullGridItem: {
        width: 12,
    },
    submitButton: {
        ...modalButtonsActionStyles.submitButton,
    },
    cancelButton: {
        ...modalButtonsActionStyles.cancelButton,
    },
};

const formId = "moreInfoButtonForm";

const MoreInfoButton: FC<Props> = ({
    requestMoreInfo,
    extendedStyles,
    productContext,
    isProductDetailsPage,
    isCurrentSelectedProductInPDP,
}) => {
    const toasterContext = useContext(ToasterContext);
    const history = useHistory();
    const [styles] = useState(() => mergeWithCss(moreInfoButtonStyle, extendedStyles));
    const [isModalOpen, setIsModalOpen] = useState(false);
    // Form fields
    const [firstName, setFirstName] = useState("");
    const [firstNameError, setFirstNameError] = useState("");
    const [lastName, setLastName] = useState("");
    const [lastNameError, setLastNameError] = useState("");
    const [company, setCompany] = useState("");
    const [phoneNumber, setPhoneNumber] = useState("");
    const [phoneNumberError, setPhoneNumberError] = useState("");
    const [email, setEmail] = useState("");
    const [emailError, setEmailError] = useState("");
    const [description, setDescription] = useState("");
    const [descriptionError, setDescriptionError] = useState("");
    const pageUrl = useCurrentPageHref();

    const handleChangeFirstName = (event: React.ChangeEvent<HTMLInputElement>) => {
        setFirstName(event.target.value);
    };
    const handleChangeLastName = (event: React.ChangeEvent<HTMLInputElement>) => {
        setLastName(event.target.value);
    };
    const handleChangePhoneNumber = (event: React.ChangeEvent<HTMLInputElement>) => {
        setPhoneNumber(event.target.value);
    };
    const handleChangeCompany = (event: React.ChangeEvent<HTMLInputElement>) => {
        setCompany(event.target.value);
    };
    const handleChangeDescription = (event: React.ChangeEvent<HTMLTextAreaElement>) => {
        setDescription(event.target.value);
    };
    const handleChangeEmail = (event: React.ChangeEvent<HTMLInputElement>) => {
        setEmail(event.target.value);
    };

    const toggleModal = () => {
        resetFields();
        setIsModalOpen(!isModalOpen);
    };

    const navigateToPdp = () => {
        const url = productContext.productInfo.productDetailPath;
        if (!url) {
            return;
        }

        history.push(url);
    };

    const resetFields = () => {
        setFirstName("");
        setLastName("");
        setCompany("");
        setPhoneNumber("");
        setEmail("");
        setDescription("");
    };

    const handleFormSubmit: React.MouseEventHandler<HTMLButtonElement> = event => {
        event.preventDefault();
        const errors = xValidateOnSubmit(formId);

        if (errors.length > 0) {
            return;
        }
        requestMoreInfo({
            firstName,
            lastName,
            company,
            phoneNumber,
            email,
            additionalNotes: description,
            pageUrl,
            onSuccess: () => {
                setIsModalOpen(false);
                toasterContext.addToast({ body: siteMessage("Custom_Request_Info_Success"), messageType: "success" });
            },
            onError: () => {
                setIsModalOpen(false);
                toasterContext.addToast({ body: siteMessage("Custom_Request_Info_Failed"), messageType: "danger" });
            },
        });
    };
    // XNG-Changes
    const moreInfoButtonAction = (
        <>
            <Button {...styles.cancelButton} onClick={toggleModal}>
                {translate("Cancel")}
            </Button>
            <Button {...styles.submitButton} onClick={handleFormSubmit}>
                {translate("Submit")}
            </Button>
        </>
    );
    // XNG-Changes

    return (
        <>
            {isProductDetailsPage && isCurrentSelectedProductInPDP ? (
                <Button onClick={toggleModal} {...styles.button} aria-label="Request More Info">
                    {translate("Request More Info")}
                </Button>
            ) : (
                <Button onClick={navigateToPdp} {...styles.defaultButton} aria-label="Request More Info">
                    {translate("More Info")}
                </Button>
            )}
            {/* //XNG-Changes */}
            <Modal
                headline={translate("Request More Information")}
                actions={moreInfoButtonAction}
                isOpen={isModalOpen}
                handleClose={toggleModal}
                {...styles.modal}
            >
                <StyledForm>
                    <GridContainer {...styles.formContainer} data-test-selector="moreInfoForm">
                        <GridItem {...styles.halfGridItem}>
                            <TextField
                                label={translate("First Name")}
                                value={firstName}
                                error={firstNameError}
                                onChange={handleChangeFirstName}
                                type="firstName"
                                required={true}
                                {...{ formId }}
                            />
                        </GridItem>
                        <GridItem {...styles.halfGridItem}>
                            <TextField
                                label={translate("Last Name")}
                                value={lastName}
                                error={lastNameError}
                                onChange={handleChangeLastName}
                                type="lastName"
                                required={true}
                                {...{ formId }}
                            />
                        </GridItem>
                        <GridItem {...styles.halfGridItem}>
                            <TextField label={translate("Company")} value={company} onChange={handleChangeCompany} />
                        </GridItem>
                        <GridItem {...styles.halfGridItem}>
                            <TextField
                                label={translate("Phone Number")}
                                value={phoneNumber}
                                error={phoneNumberError}
                                onChange={handleChangePhoneNumber}
                                type="phone"
                                required={true}
                                {...{ formId }}
                            />
                        </GridItem>
                        <GridItem {...styles.fullGridItem}>
                            <TextField
                                label={translate("Email")}
                                value={email}
                                error={emailError}
                                type="email"
                                onChange={handleChangeEmail}
                                required={false}
                                {...{ formId }}
                            />
                        </GridItem>
                        <GridItem {...styles.fullGridItem}>
                            <TextArea
                                label={translate("Description")}
                                value={description}
                                onChange={handleChangeDescription}
                                error={descriptionError}
                                data-test-selector="productShareYourMessage"
                                required={true}
                                {...{ formId }}
                            />
                        </GridItem>
                        <GridItem {...styles.fullGridItem}>
                            <TextField
                                label={translate("Page Url")}
                                value={pageUrl}
                                type="url"
                                required={true}
                                {...{ formId }}
                                disabled
                            />
                        </GridItem>
                    </GridContainer>
                </StyledForm>
            </Modal>
        </>
    );
};

export default withProductContext(connect(mapStateToProps, mapDispatchToProps)(MoreInfoButton));
